import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { RegisterOrigin } from '@pflegia/types/api/candidate/register-origin';
import { ApplicationSource } from '@pflegia/types/common/enums/application-source.enum';
import { JobDetailsNavigationSource } from '@pflegia/types/common/enums/job-details-navigation-source.enum';
import { UTM_KEYS } from '@pflegia/ui/application/marketing/utm/constants/utm-keys';
import { useAuth } from '@pflegia/ui/contexts/auth.context';

import { JOB, JOB_DETAILS } from '@shared/application/constants';

import { useAnonymized } from './use-anonymize';
import { useRouterHelper } from './use-router-helper';

/**
 * Returns navigation helper properties for job-related routes.
 *
 * @returns {Object} Navigation helper object containing:
 * - basePath: Either JOB or JOB_DETAILS based on user authentication and page context
 * - source: Navigation source (JobBoard, PremiumLandingPage, or undefined)
 * - registerOrigin: Set to PremiumLandingPage if source is PremiumLandingPage
 * - applicationSource: Application source based on current route and query params:
 *
 * Used to determine navigation props and track user journey through the application.
 */
export const useNavigationHelper = (): {
  basePath: string;
  source: string | undefined;
  registerOrigin: RegisterOrigin | undefined;
  applicationSource: string | undefined;
  slimmed: boolean | undefined;
  utmParams: Record<string, string | undefined>;
} => {
  const {
    asPath,
    query: { registerOrigin: queryRegisterOrigin, source: querySource, applicationSource: queryApplicationSource, ...restQuery },
  } = useRouter();

  const hashParams = useMemo(() => {
    if (typeof window === 'undefined' || !asPath) return {};
    const [, hash] = asPath.split('#');
    if (!hash) return {};
    return Object.fromEntries(new URLSearchParams(hash));
  }, [asPath]);

  const utmParams = useMemo(() => {
    const params: Record<string, string | undefined> = {};

    for (const key of UTM_KEYS) {
      const value = hashParams[key] || restQuery[key];
      if (value) params[key] = Array.isArray(value) ? value[0] : value;
    }

    return params;
  }, [hashParams, restQuery]);

  const registerOrigin = (hashParams.registerOrigin || queryRegisterOrigin) as RegisterOrigin | undefined;
  const source = (hashParams.source || querySource) as string | undefined;
  const applicationSource = (hashParams.applicationSource || queryApplicationSource) as string | undefined;
  const isAnonymizedJobPage = useAnonymized();
  const authenticated = useAuth();
  const routeHelper = useRouterHelper();

  const { isSeoLandingPage, isJobBoard, isBlog } = routeHelper;
  const fromPremiumLandingPage = registerOrigin === RegisterOrigin.PremiumLandingPage || isSeoLandingPage;

  const computedSource = useMemo(() => {
    if (isJobBoard) return JobDetailsNavigationSource.JobBoard;
    if (fromPremiumLandingPage) return JobDetailsNavigationSource.PremiumLandingPage;
    if (isBlog) return JobDetailsNavigationSource.Blog;
    return source as JobDetailsNavigationSource | undefined;
  }, [isJobBoard, fromPremiumLandingPage, isBlog, source]);

  const computedApplicationSource = useMemo(() => {
    const { isAnyJobPage, isExternalJob, isSalaryCalculatorPage, isTransparentJobBoard, isTransparentJobDetails, isTransparentPath } =
      routeHelper;
    if (isBlog) return ApplicationSource.Blog;
    if (isSalaryCalculatorPage) return ApplicationSource.SalaryCalculator;
    if (applicationSource) {
      if (applicationSource === ApplicationSource.TransparentJobBoard && isTransparentJobDetails)
        return ApplicationSource.TransparentJobDetailsPage;
      return applicationSource as string | undefined;
    }
    if (fromPremiumLandingPage) return ApplicationSource.PremiumLandingPage;
    if (isJobBoard) return authenticated ? ApplicationSource.JobBoard : ApplicationSource.UnconfirmedJobBoard;
    if (isTransparentJobDetails) return ApplicationSource.TransparentJobDetailsPage;
    if (isTransparentJobBoard) return ApplicationSource.TransparentJobBoard;
    if (isExternalJob) return ApplicationSource.MultipostingRegistration;
    // These two must be last as they are the most generic
    if (isAnyJobPage) return ApplicationSource.JobDetailsPage;
    if (isTransparentPath) return ApplicationSource.TransparentRegistration;
    return undefined;
  }, [routeHelper, isBlog, applicationSource, fromPremiumLandingPage, isJobBoard, authenticated]);

  return {
    basePath: (isAnonymizedJobPage && !fromPremiumLandingPage) || authenticated ? JOB : JOB_DETAILS,
    source: source ?? computedSource,
    registerOrigin: getRegisterOrigin(computedSource),
    applicationSource: computedApplicationSource,
    slimmed: routeHelper.isSlimmed,
    utmParams,
  };
};

const getRegisterOrigin = (source?: JobDetailsNavigationSource) => {
  switch (source) {
    case JobDetailsNavigationSource.Blog:
      return RegisterOrigin.Blog;
    case JobDetailsNavigationSource.PremiumLandingPage:
      return RegisterOrigin.PremiumLandingPage;
    default:
      return undefined;
  }
};
